import { useEffect, useRef, useState } from 'react';
import {
  ApiError,
  TAG_COLORS,
  createTag,
  deleteTag,
  updateTag,
  type CameraTag,
  type CameraTagWithCount,
  type TagColor,
} from '../lib/api';
import { useBahasa } from '../i18n';
import { DialogHapus } from './DialogHapus';

/**
 * Chip tag (§G11.1).
 *
 * Warnanya token §G3, dan **tidak satu pun warna status**: `green-600` /
 * `amber-500` / `red-600` sudah berarti sehat / waspada / bermasalah (§G3.1),
 * dan tag merah di sebelah chip status merah pada kartu yang sama membuat orang
 * membaca "kamera ini bermasalah" dari sesuatu yang cuma berarti "kamera ini di
 * gerbang utama". Satu kosakata untuk satu arti — dan warna termasuk kosakata.
 */
const KELAS_WARNA: Record<TagColor, string> = {
  navy: 'border-content/30 text-content',
  primary: 'border-accent-text/40 text-accent-text',
  accent: 'border-accent-highlight/50 text-accent-highlight',
  tint: 'border-transparent bg-subtle text-content',
  muted: 'border-line text-muted',
};

/** Warna yang tidak dikenal jatuh ke `muted`, bukan hilang tanpa jejak. */
function kelas(color: string): string {
  return KELAS_WARNA[color as TagColor] ?? KELAS_WARNA.muted;
}

export function TagChip({ tag, className = '' }: { tag: CameraTag; className?: string }) {
  return (
    <span
      className={`inline-flex max-w-[10rem] items-center truncate rounded border px-1.5 py-px text-xs leading-4 ${kelas(tag.color)} ${className}`}
      title={tag.name}
    >
      {tag.name}
    </span>
  );
}

/**
 * Chip pada kartu kamera. **Maksimal tiga terlihat**, sisanya "+N".
 *
 * Kartu `Small` selebar ±96px tidak muat memuat tiga kata tanpa mengecilkan
 * nama kameranya, dan nama kamera yang menang — di sana chip diganti titik
 * berwarna yang tetap bisa diarahkan untuk membaca namanya.
 */
export function TagChips({ tags, compact = false }: { tags: CameraTag[]; compact?: boolean }) {
  if (tags.length === 0) return null;

  if (compact) {
    return (
      <span className="flex items-center gap-1" title={tags.map((t) => t.name).join(' · ')}>
        {tags.slice(0, 3).map((t) => (
          <span
            key={t.id}
            aria-hidden
            className={`inline-block h-2 w-2 rounded-full border ${kelas(t.color)}`}
          />
        ))}
        {/* Titik tanpa nama tidak bisa dibaca pembaca layar — namanya tetap
            ada sebagai teks, cuma disembunyikan dari mata. */}
        <span className="sr-only">{tags.map((t) => t.name).join(', ')}</span>
      </span>
    );
  }

  const terlihat = tags.slice(0, 3);
  const sisa = tags.length - terlihat.length;

  return (
    <span className="flex flex-wrap items-center gap-1">
      {terlihat.map((t) => (
        <TagChip key={t.id} tag={t} />
      ))}
      {sisa > 0 && (
        <span className="text-xs text-muted" title={tags.slice(3).map((t) => t.name).join(', ')}>
          +{sisa}
        </span>
      )}
    </span>
  );
}

/**
 * Saringan tag di bilah atas `/cameras` (§G11.1).
 *
 * **DAN, bukan ATAU.** "Mana yang di luar dan penting" pertanyaan yang masuk
 * akal; "outdoor atau critical" hampir selalu berarti orangnya salah pilih, dan
 * hasilnya justru lebih banyak daripada tanpa saringan — kebalikan dari yang
 * dia maksud. Kalimat "semua tag terpilih" ditulis di layar supaya aturannya
 * tidak perlu ditebak.
 */
export function TagFilter({
  tags,
  terpilih,
  onChange,
}: {
  tags: CameraTagWithCount[];
  terpilih: string[];
  onChange: (names: string[]) => void;
}) {
  const { t } = useBahasa();
  const [buka, setBuka] = useState(false);
  const kotak = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!buka) return;
    function luar(e: MouseEvent) {
      if (kotak.current && !kotak.current.contains(e.target as Node)) setBuka(false);
    }
    function esc(e: KeyboardEvent) {
      if (e.key === 'Escape') setBuka(false);
    }
    document.addEventListener('mousedown', luar);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', luar);
      document.removeEventListener('keydown', esc);
    };
  }, [buka]);

  if (tags.length === 0) return null;

  function toggle(name: string) {
    onChange(terpilih.includes(name) ? terpilih.filter((n) => n !== name) : [...terpilih, name]);
  }

  return (
    <div ref={kotak} className="relative">
      <button
        type="button"
        onClick={() => setBuka((b) => !b)}
        aria-expanded={buka}
        className="min-h-tap rounded-md border border-line bg-surface px-3 text-sm"
      >
        {terpilih.length === 0
          ? t('Tags')
          : t('Tags: {n}', { n: terpilih.length })}
      </button>

      {buka && (
        <div className="absolute right-0 z-20 mt-1 w-64 rounded-md border border-line bg-surface p-2 shadow-lg">
          <p className="px-1 pb-1 text-xs text-muted">
            {t('Cameras must have every selected tag.')}
          </p>
          <ul className="max-h-64 overflow-y-auto">
            {tags.map((tag) => (
              <li key={tag.id}>
                <label className="flex min-h-tap cursor-pointer items-center gap-2 rounded px-1 text-sm hover:bg-subtle">
                  <input
                    type="checkbox"
                    checked={terpilih.includes(tag.name)}
                    onChange={() => toggle(tag.name)}
                  />
                  <TagChip tag={tag} className="shrink-0" />
                  <span className="ml-auto text-xs text-muted">{tag.cameraCount}</span>
                </label>
              </li>
            ))}
          </ul>
          {terpilih.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1 w-full rounded px-1 py-1 text-left text-sm underline underline-offset-2"
            >
              {t('Clear tag filter')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Pemilih tag di dalam form kamera — daftar centang, tanpa dropdown. */
export function TagPicker({
  tags,
  terpilih,
  onChange,
}: {
  tags: CameraTagWithCount[];
  terpilih: string[];
  onChange: (ids: string[]) => void;
}) {
  const { t } = useBahasa();
  if (tags.length === 0) {
    return <p className="text-xs text-muted">{t('No tags have been created yet.')}</p>;
  }
  return (
    <ul className="flex flex-wrap gap-1.5">
      {tags.map((tag) => {
        const aktif = terpilih.includes(tag.id);
        return (
          <li key={tag.id}>
            <button
              type="button"
              aria-pressed={aktif}
              onClick={() =>
                onChange(aktif ? terpilih.filter((i) => i !== tag.id) : [...terpilih, tag.id])
              }
              className={`inline-flex min-h-tap items-center rounded-md border px-2 text-xs ${
                aktif ? kelas(tag.color) : 'border-line text-muted'
              } ${aktif ? 'ring-1 ring-current' : ''}`}
            >
              {tag.name}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Panel "Kelola tag" — Admin dan Operator (§G11.1): dialah yang memasang
 * kameranya, dan menggolongkannya bagian dari memasang.
 *
 * Menghapus **menyebut berapa kamera akan kehilangannya**. Angka itu
 * satu-satunya yang membedakan "tag salah ketik yang belum dipakai" dari "tag
 * yang menempel di 60 kamera", dan tanpa itu konfirmasinya cuma ritual.
 */
export function TagManager({
  tags,
  onClose,
  onChanged,
}: {
  tags: CameraTagWithCount[];
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const { t } = useBahasa();
  const [nama, setNama] = useState('');
  const [warna, setWarna] = useState<TagColor>('muted');
  const [galat, setGalat] = useState<string | null>(null);
  const [sibuk, setSibuk] = useState(false);
  const [konfirmasi, setKonfirmasi] = useState<CameraTagWithCount | null>(null);

  async function jalankan(fn: () => Promise<unknown>) {
    setSibuk(true);
    setGalat(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      // 409 punya kalimatnya sendiri dari server ("names are not
      // case-sensitive") — dipakai apa adanya, karena di layar "POP" dan "pop"
      // memang terlihat sebagai dua nama berbeda.
      setGalat(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setSibuk(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('Manage tags')}
        className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-lg border border-line bg-surface p-4"
      >
        <h2 className="text-lg">{t('Manage tags')}</h2>
        <p className="mt-1 text-xs text-muted">
          {t('Tags group cameras. They never change who can see a camera.')}
        </p>

        {galat && (
          <p role="alert" className="mt-3 rounded-md border border-red-600 px-3 py-2 text-sm">
            {galat}
          </p>
        )}

        <form
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const bersih = nama.trim();
            if (bersih === '') return;
            void jalankan(async () => {
              await createTag(bersih, warna);
              setNama('');
            });
          }}
        >
          <label className="flex-1 text-sm">
            <span className="mb-1 block text-muted">{t('New tag')}</span>
            <input
              value={nama}
              onChange={(e) => setNama(e.target.value)}
              maxLength={48}
              placeholder="outdoor"
              className="min-h-tap w-full rounded-md border border-line bg-surface px-3 text-sm"
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block text-muted">{t('Colour')}</span>
            <select
              value={warna}
              onChange={(e) => setWarna(e.target.value as TagColor)}
              className="min-h-tap rounded-md border border-line bg-surface px-2 text-sm"
            >
              {TAG_COLORS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            disabled={sibuk || nama.trim() === ''}
            className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast disabled:opacity-50"
          >
            {t('Add')}
          </button>
        </form>

        <ul className="mt-4 divide-y divide-line border-t border-line">
          {tags.map((tag) => (
            <li key={tag.id} className="flex items-center gap-2 py-2">
              <TagChip tag={tag} />
              <span className="text-xs text-muted">
                {t('{n} cameras', { n: tag.cameraCount })}
              </span>
              <select
                value={tag.color}
                disabled={sibuk}
                onChange={(e) =>
                  void jalankan(() => updateTag(tag.id, { color: e.target.value as TagColor }))
                }
                className="ml-auto min-h-tap rounded-md border border-line bg-surface px-1 text-xs"
                aria-label={t('Colour of {name}', { name: tag.name })}
              >
                {TAG_COLORS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={sibuk}
                onClick={() => setKonfirmasi(tag)}
                className="min-h-tap rounded-md border border-line px-2 text-xs"
              >
                {t('Delete')}
              </button>
            </li>
          ))}
        </ul>

        {/* Panel konfirmasi sendiri di sini sudah diganti dialog bersama §G17.
            Kalimat "berapa kamera yang kehilangan tag ini" tetap dibawa masuk —
            itu satu-satunya angka yang menjelaskan seberapa besar akibatnya,
            dan ia tidak ada di layar mana pun selain di sini. */}
        {konfirmasi && (
          <DialogHapus
            nama={konfirmasi.name}
            judul={t('Delete tag {nama}', { nama: konfirmasi.name })}
            keterangan={
              konfirmasi.cameraCount === 0
                ? t('No camera uses it.')
                : t('{n} cameras will lose this tag. The cameras themselves are not touched.', {
                    n: konfirmasi.cameraCount,
                  })
            }
            onBatal={() => setKonfirmasi(null)}
            onHapus={async (k) => {
              await deleteTag(konfirmasi.id, k);
              setKonfirmasi(null);
              await onChanged();
            }}
          />
        )}

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="min-h-tap rounded-md border border-line px-4 text-sm"
          >
            {t('Close')}
          </button>
        </div>
      </div>
    </div>
  );
}
